import { useRef, Suspense, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial, Preload } from "@react-three/drei";

// Utility to check WebGL support
function isWebGLAvailable() {
  try {
    const canvas = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    );
  } catch (e) {
    return false;
  }
}

// Manually generate a sphere of points
const generateManualSphere = (count, radius) => {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = radius;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos((Math.random() * 2) - 1);

    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  return positions;
};

const Stars = () => {
  const ref = useRef();
  const sphere = useMemo(() => generateManualSphere(5000, 1.2), []);

  useFrame((state, delta) => {
    ref.current.rotation.x -= delta / 10;
    ref.current.rotation.y -= delta / 15;
  });

  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled>
        <PointMaterial
          transparent
          color={typeof window !== "undefined" && window.__starsColor ? window.__starsColor : "#ffffff"}
          size={0.008}
          sizeAttenuation
          depthWrite={false}
        />
      </Points>
    </group>
  );
};

const StarsCanvas = () => {
  const [webgl, setWebgl] = useState(true);

  useEffect(() => {
    setWebgl(isWebGLAvailable());
    // Theme-aware star color
    const isDark =
      document.body.classList.contains("dark") ||
      (window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    // Try to get styled-components theme if available
    let color = "#ffffff";
    try {
      const theme = window.__themeObject;
      if (theme && theme.colors) {
        if (
          theme.colors.background1 === "#ffffff" ||
          theme.colors.text === "#1e1e2f"
        ) {
          // Light mode
          color = "#5a189a"; // darker purple for better visibility in light mode
        } else {
          // Dark mode
          color = "#ffffff";
        }
      } else if (!isDark) {
        color = "#5a189a";
      }
    } catch (e) {}
    window.__starsColor = color;
  }, []);

  if (!webgl) {
    // fallback: just a dark background
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: -1,
          background: "#050816",
          pointerEvents: "none",
        }}
      />
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: -1,
        pointerEvents: "none", // prevent interference
      }}
    >
      <Canvas camera={{ position: [0, 0, 1] }}>
        <Suspense fallback={null}>
          <Stars />
        </Suspense>
        <Preload all />
      </Canvas>
    </div>
  );
};

export default StarsCanvas;
  
